import React, { useCallback } from 'react';
import { DataStore } from '@aws-amplify/datastore';
import {
  PageSettingOverride,
  UserDocumentVersionSettings,
  UserNotationsStatus,
} from '@alucio/aws-beacon-amplify/src/models';
import {
  canToggleHiddenSlide,
  getNormalizedPageSettingOverrides,
  getPageSettingsWithHiddenOverrideToggle,
} from 'src/utils/hiddenSlides';
import {
  FolderItemORM,
  isCustomDeckORM,
  isDocumentVersionORM,
  isFolderItemORM,
  PresentablePage,
} from 'src/types/types';
import { useDispatch, useSelector } from 'src/state/redux';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import RequiredSlidesHiddenConfirmation
  from 'src/components/DNA/Modal/DNAPresentationBuilder/RequiredSlidesHiddenConfirmation';
import ActiveUser from 'src/state/global/ActiveUser';
import { userDocumentVersionSettingsActions } from 'src/state/redux/slice/userDocumentVersionSettings';
import { selUserDocumentVersionSettingsMap } from 'src/state/redux/selector/document';

type ToggleHiddenSlideOptions = {
  folderItemORM?: FolderItemORM,
}

type HiddenSlideSettingsScope = {
  key: string,
  documentId?: string,
  documentVersionId?: string,
  customDeckId?: string,
  settings?: UserDocumentVersionSettings,
}

const HIDDEN_SLIDE_SAVE_DEBOUNCE_MS = 2500;

const pendingUserDocumentVersionSettingsSaves = new Map<string, {
  scope: Omit<HiddenSlideSettingsScope, 'settings'>,
  settings?: UserDocumentVersionSettings,
  pageSettings: PageSettingOverride[],
  timer?: ReturnType<typeof setTimeout>,
  isSaving?: boolean,
}>();

const getPendingPageSettings = (
  scope: HiddenSlideSettingsScope,
): PageSettingOverride[] =>
  // Reads pending edits first so rapid toggles build on unsaved local state.
  pendingUserDocumentVersionSettingsSaves.get(scope.key)?.pageSettings ??
  scope.settings?.pageSettings ??
  [];

const getUserDocumentVersionSettingsWithPageSettings = ({
  scope,
  settings,
  pageSettings,
}: {
  scope: Omit<HiddenSlideSettingsScope, 'settings'>,
  settings?: UserDocumentVersionSettings,
  pageSettings: PageSettingOverride[],
}): UserDocumentVersionSettings => {
  const user = ActiveUser.user;
  const now = new Date().toISOString();
  const normalizedPageSettings = getNormalizedPageSettingOverrides(pageSettings);

  if (!user) {
    throw new Error('Active user is not set when trying to save userDocumentVersionSettings');
  }

  if (settings) {
    return UserDocumentVersionSettings.copyOf(settings, draft => {
      draft.updatedAt = now;
      draft.updatedBy = user.id;
      draft.pageSettings = normalizedPageSettings;
    });
  }

  return new UserDocumentVersionSettings({
    tenantId: user.tenantId,
    createdAt: now,
    createdBy: user.id,
    updatedAt: now,
    updatedBy: user.id,
    documentId: scope.documentId,
    documentVersionId: scope.documentVersionId,
    customDeckId: scope.customDeckId,
    pageSettings: normalizedPageSettings,
    status: UserNotationsStatus.ACTIVE,
  });
};

function haveSamePageSettings(
  pageSettings: PageSettingOverride[] = [],
  normalizedPageSettings: PageSettingOverride[] = [],
): boolean {
  return pageSettings.length === normalizedPageSettings.length &&
    pageSettings.every((pageSetting, index) =>
      pageSetting.pageId === normalizedPageSettings[index]?.pageId &&
      pageSetting.hidden === normalizedPageSettings[index]?.hidden);
}

async function getLatestUserDocumentVersionSettings(
  settings?: UserDocumentVersionSettings,
): Promise<UserDocumentVersionSettings | undefined> {
  if (!settings?.id) {
    return settings;
  }

  return (await DataStore.query(UserDocumentVersionSettings, settings.id)) ?? settings;
}

async function saveUserDocumentVersionSettings(
  settings: UserDocumentVersionSettings,
): Promise<UserDocumentVersionSettings> {
  let savedSettings = await DataStore.save(settings);

  for (let attempt = 0; attempt < 2; attempt += 1) {
    const normalizedPageSettings = getNormalizedPageSettingOverrides(savedSettings.pageSettings ?? []);
    if (haveSamePageSettings(savedSettings.pageSettings ?? [], normalizedPageSettings)) {
      return savedSettings;
    }

    const user = ActiveUser.user;
    const now = new Date().toISOString();
    savedSettings = await DataStore.save(UserDocumentVersionSettings.copyOf(savedSettings, draft => {
      if (user) {
        draft.updatedAt = now;
        draft.updatedBy = user.id;
      }
      draft.pageSettings = normalizedPageSettings;
    }));
  }

  return savedSettings;
}

export const useHiddenSlideToggle = (options: ToggleHiddenSlideOptions = {}) => {
  const dispatch = useDispatch();
  const settingsMap = useSelector(selUserDocumentVersionSettingsMap);

  const getSettingsScope = useCallback((page: PresentablePage): HiddenSlideSettingsScope => {
    const presentableORM = options.folderItemORM ?? page.presentableGroup.documentVersionORM;

    if (isFolderItemORM(presentableORM)) {
      const itemORM = presentableORM.relations.itemORM;

      if (isCustomDeckORM(itemORM)) {
        return {
          key: `customDeck:${itemORM.model.id}`,
          customDeckId: itemORM.model.id,
          settings: settingsMap.customDeckLevel[itemORM.model.id],
        };
      }

      if (isDocumentVersionORM(itemORM)) {
        return {
          key: `documentVersion:${itemORM.model.id}`,
          documentId: itemORM.model.documentId,
          documentVersionId: itemORM.model.id,
          settings: settingsMap.documentVersionLevel[itemORM.model.id],
        };
      }
    }

    const documentVersion = page.documentVersionORM.model;

    return {
      key: `documentVersion:${documentVersion.id}`,
      documentId: documentVersion.documentId,
      documentVersionId: documentVersion.id,
      settings: settingsMap.documentVersionLevel[documentVersion.id],
    };
  }, [options.folderItemORM, settingsMap]);

  const savePendingHiddenSlideSettings = useCallback((scopeKey: string) => {
    const pendingSave = pendingUserDocumentVersionSettingsSaves.get(scopeKey);

    if (!pendingSave || pendingSave.isSaving) {
      return;
    }

    pendingUserDocumentVersionSettingsSaves.set(scopeKey, {
      ...pendingSave,
      isSaving: true,
      timer: undefined,
    });

    getLatestUserDocumentVersionSettings(pendingSave.settings).then((latestSettings) => {
      // Re-read the DataStore record before saving to avoid overwriting a fresher local copy.
      const settingsToSave = getUserDocumentVersionSettingsWithPageSettings({
        scope: pendingSave.scope,
        settings: latestSettings,
        pageSettings: pendingSave.pageSettings,
      });

      return saveUserDocumentVersionSettings(settingsToSave);
    }).then((savedSettings) => {
      const latestPendingSave = pendingUserDocumentVersionSettingsSaves.get(scopeKey);

      if (latestPendingSave?.pageSettings === pendingSave.pageSettings) {
        pendingUserDocumentVersionSettingsSaves.delete(scopeKey);
      } else if (latestPendingSave) {
        // A newer toggle arrived while saving; debounce another write with the saved record as base.
        const timer = setTimeout(() => {
          savePendingHiddenSlideSettings(scopeKey);
        }, HIDDEN_SLIDE_SAVE_DEBOUNCE_MS);

        pendingUserDocumentVersionSettingsSaves.set(scopeKey, {
          ...latestPendingSave,
          settings: savedSettings,
          timer,
          isSaving: false,
        });
      }

      dispatch(userDocumentVersionSettingsActions.upsert(savedSettings));
    }).catch(() => {
      const failedPendingSave = pendingUserDocumentVersionSettingsSaves.get(scopeKey);
      if (failedPendingSave) {
        pendingUserDocumentVersionSettingsSaves.set(scopeKey, {
          ...failedPendingSave,
          isSaving: false,
        });
      }
    });
  }, [dispatch]);

  const queueHiddenSlideSettingsSave = useCallback(({
    scope,
    pageSettings,
  }: {
    scope: HiddenSlideSettingsScope,
    pageSettings: PageSettingOverride[],
  }) => {
    const existingPendingSave = pendingUserDocumentVersionSettingsSaves.get(scope.key);

    if (existingPendingSave?.timer) {
      clearTimeout(existingPendingSave.timer);
    }

    // Hidden toggles can be rapid, so collapse them into one UserDocumentVersionSettings write.
    const timer = setTimeout(() => {
      savePendingHiddenSlideSettings(scope.key);
    }, HIDDEN_SLIDE_SAVE_DEBOUNCE_MS);

    pendingUserDocumentVersionSettingsSaves.set(scope.key, {
      scope: {
        key: scope.key,
        documentId: scope.documentId,
        documentVersionId: scope.documentVersionId,
        customDeckId: scope.customDeckId,
      },
      settings: existingPendingSave?.settings ?? scope.settings,
      pageSettings,
      timer,
      isSaving: existingPendingSave?.isSaving,
    });
  }, [savePendingHiddenSlideSettings]);

  const persistHiddenToggle = useCallback((page: PresentablePage, nextIsHidden: boolean) => {
    const scope = getSettingsScope(page);
    const pageId = page.page.pageId;

    if (!pageId) {
      return;
    }

    const pageSettings = getPageSettingsWithHiddenOverrideToggle({
      pageSettings: getPendingPageSettings(scope),
      pageId,
      nextIsHidden,
    });

    queueHiddenSlideSettingsSave({
      scope,
      pageSettings,
    });
  }, [getSettingsScope, queueHiddenSlideSettingsSave]);

  return useCallback((page: PresentablePage) => {
    if (!canToggleHiddenSlide(page.page) && !page.page.isHidden) {
      return;
    }

    const nextIsHidden = !page.page.isHidden;
    const persist = () => persistHiddenToggle(page, nextIsHidden);

    if (nextIsHidden && page.page.isRequired) {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (modalProps) => (
          <RequiredSlidesHiddenConfirmation
            {...modalProps}
            onSave={persist}
          />
        ),
      }));
      return;
    }

    persist();
  }, [dispatch, persistHiddenToggle]);
};

export default useHiddenSlideToggle;
